import { Component, h, Listen, Prop, Watch, Event, EventEmitter } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { Trackable } from '../../types/global';

export interface SocialLink extends Trackable {
  iconPath: string;
  href: string;
  mouseover: string;
}

interface UiCmsSocialsBody {
  text: string;
  links: SocialLink[];
  position: 'left' | 'right';
  trackingArea?: 'Header' | 'Navigation' | 'Content' | 'Footer' | 'Tooltip' | 'Sticky Toolbar';
}

@Component({
  tag: 'ui-cms-socials',
  styleUrl: 'ui-cms-socials.scss',
})
export class UiCmsSocials {
  @Prop({ mutable: true }) body: UiCmsSocialsBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  /**
   * Defines the position of the host element.
   */
  @Prop() position: UiCmsSocialsBody['position'] = 'right';

  /**
   * Event responsible for the click action on a link
   */
  @Event({ eventName: 'linkClick' }) linkClick: EventEmitter<{
    link: SocialLink;
  }>;

  @Listen('linkClick')
  linkClickHandler(event: CustomEvent<{ link: SocialLink }>) {
    const { link } = event.detail;

    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Social Network Icon',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Link',
        elementLabel: link.iconPath || '',
        externalTarget: link.href || '',
      },
      category: {
        area: this.body.trackingArea ? this.body.trackingArea : 'Content',
      },
    };

    window.ui?.services?.tracking?.track(trackingEvent);
  }

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
  }

  private onTouchStart(link: SocialLink) {
    if (link.href) {
      window.open(link.href);
    }
    this.onLinkClick(link);
  }

  private onLinkClick(link: SocialLink) {
    this.linkClick.emit({ link });
  }

  render() {
    return (
      <div
        class={{
          'social-links-wrapper': true,
          [`align-${this.body.position ? this.body.position : this.position}`]: true,
        }}
      >
        {this.body.text && <span>{this.body.text}</span>}
        {this.body.links?.length > 0 &&
          this.body.links?.map((link, index) => {
            const buttonClass = {
              'social-btn': true,
              [`social-btn--${link.iconPath.replace('union-investment/system-social/', '')}`]: !!link.iconPath,
            };
            return (
              <sd-link
                key={index}
                class={buttonClass}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  this.onLinkClick(link);
                }}
                onTouchStart={() => {
                  this.onTouchStart(link);
                }}
              >
                <span class="sr-only">{link.mouseover}</span>
                <sd-icon
                  library={window.ui.cmsState.iconLibrary}
                  name={link.iconPath}
                  color={link.iconPath === 'union-investment/system-social/blog' ? 'primary' : 'white'}
                ></sd-icon>
              </sd-link>
            );
          })}
      </div>
    );
  }
}
